import { Fragment, ReactElement, useCallback, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Button, Tooltip } from 'antd';
import { HistoryOutlined } from '@ant-design/icons';
import { Guard } from '@ohpen/react-authz';

import { ChangeLogDrawer } from './components';
import { ChangeLogTarget } from './interfaces';

/** The record a row action opens the log of, as the backend identifies it. */
type ChangeLogRecord = Pick<ChangeLogTarget, 'id' | 'name'>;

/**
 * The "View change log" row action of a list and the drawer it opens. `toRecord` must be stable, a
 * module-level function, or every row action re-renders on each render of the list.
 */
export const useChangeLogAction = <T,>(entityType: ChangeLogTarget['entityType'] | undefined, toRecord: (record: T) => ChangeLogRecord) => {
  const { t } = useTranslation();
  const [target, setTarget] = useState<ChangeLogTarget | null>(null);

  const changeLogAction = useCallback(
    (record: T): ReactElement =>
      entityType ? (
        <Guard key="changeLog" flags={['enableChangeLog']}>
          <Tooltip title={t('lender.changeLog.labels.viewChangeLog')} placement="top">
            <Button
              type="text"
              aria-label="View change log"
              icon={<HistoryOutlined />}
              onClick={() => setTarget({ entityType, ...toRecord(record) })}
            />
          </Tooltip>
        </Guard>
      ) : (
        <Fragment key="changeLog" />
      ),
    [entityType, toRecord, t],
  );

  const changeLogDrawer = <ChangeLogDrawer target={target} onClose={() => setTarget(null)} />;

  return { changeLogAction, changeLogDrawer };
};
